import { Pagination } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";
import {
  DataGridPro,
  GridColumnVisibilityModel,
  GridColDef,
  GridFilterPanel,
  GridFooterContainer,
  GridLinkOperator,
  GridPagination,
  GridRowIdGetter,
  GridRowParams,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppFilterSettings,
  AppGridId,
  GridIdsToSave,
} from "../../models/app/AppGridModels";
import {
  addGridSetting,
  useAddAppGridSettingMutation,
} from "../../redux/reducer/features/App/AppGridSlice";
import { AppState } from "../../redux/reducer/rootReducer";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { DmsManufacturer } from "../../models/features/ShoppingPortal/DmsManufacturer";
import { GridFilterModel } from "@mui/x-data-grid-pro";
import SaveFilterDialog from "./SaveFilterDialog";
import SnackBarPopup from "./SnackBarPopup";
import ManageSavedFilters from "./ManageSavedFilters";
import {
  ManageSavedFilterProps,
  useSavedFilterHandlers,
} from "./ManageSavedFilters";
import ManageShortcuts, {
  useSavedFilterShortcutHandlers,
} from "./ManageShortcuts";
import { ReactComponent as ResetFilterIcon } from "../../Assets/ResetFilter.svg";
import { ReactComponent as DisabledResetFilter } from "../../Assets/DisabledResetFilter.svg";
interface ServerAppGridProps {
  columns: GridColDef[];
  isLoading: boolean;
  data: any[];
  height?: any;
  onSelect?: any;
  selectedRows?: any;
  getRowId?: GridRowIdGetter<any>;
  setPaging: any;
  paging: any;
  totalRecords: number;
  hideFooter?: boolean;
  hidePaginator?: boolean;
  disableSelection?: boolean;
  keepNonExistentRowsSelected?: boolean | undefined;
  hideColumnFilter?: boolean;
  hideCheckBoxes?: boolean;
  hidePaginationText?: boolean;
  hidePageSize?: boolean;
  gridId?: AppGridId | undefined;
  isRowSelectable?: ((params: GridRowParams<any>) => boolean) | undefined;
  customToolbar?: boolean;
  switchLabel?: string;
  switchChecked?: boolean;
  onSwitchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  manufacturerOptions?: DmsManufacturer[];
  selectedManufacturers?: DmsManufacturer[];
  onManufacturerChange?: (selected: DmsManufacturer[]) => void;
}

const operatorsWithoutValue = new Set(["isEmpty", "isNotEmpty"]);

const isValidFilterItem = (item: any) => {
  if (!item.columnField || !item.operatorValue) {
    return false;
  }

  if (operatorsWithoutValue.has(item.operatorValue)) {
    return true;
  }

  return item.value !== undefined && item.value !== null && item.value !== "";
};

interface ToolbarProps extends ManageSavedFilterProps {
  customToolbar?: boolean;
  switchLabel?: string;
  switchChecked?: boolean;
  onSwitchChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  manufacturerOptions?: DmsManufacturer[];
  selectedManufacturers?: DmsManufacturer[];
  onManufacturerChange?: (selected: DmsManufacturer[]) => void;
  onRemoveShortcut?: (filterName: string) => void;
  onResetFilters?: () => void;
  isResetDisabled?: boolean;
  resetSignal?: number;
}

const DefaultToolbar = ({
  customToolbar,
  switchLabel,
  switchChecked,
  onSwitchChange,
  manufacturerOptions,
  selectedManufacturers,
  onManufacturerChange,
  savedFilters,
  onApplySavedFilter,
  onDeleteSavedFilter,
  onRenameSavedFilter,
  onRemoveShortcut,
  onResetFilters,
  isResetDisabled,
  resetSignal,
}: ToolbarProps) => {
  const allOption: DmsManufacturer = {
    manufacturerId: "all",
    code: "",
    name: "Select All",
    isActive: true,
  };
  const allSelected =
    (manufacturerOptions?.length ?? 0) > 0 &&
    (selectedManufacturers?.length ?? 0) === (manufacturerOptions?.length ?? 0);

  return (
    <GridToolbarContainer sx={{ bgcolor: "grey.50", py: 1.25, px: 0.5 }}>
      <Box display={"flex"} justifyContent={"space-between"} width="100%">
        <Box>
          <GridToolbarColumnsButton {...({} as any)} />
          <GridToolbarFilterButton {...({} as any)} />
          <ManageSavedFilters
            savedFilters={savedFilters}
            onApplySavedFilter={onApplySavedFilter}
            onDeleteSavedFilter={onDeleteSavedFilter}
            onRenameSavedFilter={onRenameSavedFilter}
          />
          <GridToolbarDensitySelector {...({} as any)} />
          <GridToolbarExport />
        </Box>
        <ManageShortcuts
          savedFilters={savedFilters}
          onApplySavedFilter={onApplySavedFilter}
          onRemoveShortcut={onRemoveShortcut}
          resetSignal={resetSignal}
        />
        {customToolbar ? (
          <Box display={"flex"} gap={3}>
            <Box width={300}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={[allOption, ...(manufacturerOptions || [])]}
                value={selectedManufacturers}
                onChange={(event, value) => {
                  if (value.some((v) => v.manufacturerId === "all")) {
                    if (!allSelected) {
                      onManufacturerChange?.(manufacturerOptions || []);
                    } else {
                      onManufacturerChange?.([]);
                    }
                  } else {
                    onManufacturerChange?.(value);
                  }
                }}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="medium" />}
                      checkedIcon={<CheckBoxIcon fontSize="medium" />}
                      style={{ marginRight: 5, padding: 0 }}
                      checked={
                        option.manufacturerId === "all" ? allSelected : selected
                      }
                    />
                    {option.name}
                  </li>
                )}
                style={{ width: 300 }}
                renderInput={(params) => (
                  <TextField {...params} label="Manufacturers" size="small" />
                )}
                renderTags={() => null}
              />
            </Box>
            <FormControlLabel
              checked={switchChecked}
              control={
                <Switch
                  name="jason"
                  value={switchChecked}
                  onChange={onSwitchChange}
                  size="small"
                />
              }
              label={switchLabel}
            />
            <GridToolbarQuickFilter />
          </Box>
        ) : (
          <Box>
            <Button
              size="small"
              color="error"
              onClick={onResetFilters}
              disabled={isResetDisabled}
              startIcon={
                isResetDisabled ? (
                  <DisabledResetFilter width={24} height={24} />
                ) : (
                  <ResetFilterIcon width={24} height={24} />
                )
              }
            >
              Reset Filters
            </Button>
            <GridToolbarQuickFilter />
          </Box>
        )}
      </Box>
    </GridToolbarContainer>
  );
};

interface CustomFilterPanelProps {
  onSaveFilter?: () => void;
  disableSaveFilter?: boolean;
  [key: string]: any;
}

const CustomFilterPanel = ({
  onSaveFilter,
  disableSaveFilter,
  ...props
}: CustomFilterPanelProps) => {
  const apiRef = useGridApiContext();

  return (
    <Box>
      <GridFilterPanel {...props} />
      <Box
        position="absolute"
        bottom={7}
        right={7}
        zIndex={1}
        display="flex"
        gap={1}
      >
        {onSaveFilter && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => {
              apiRef.current.hidePreferences?.();
              onSaveFilter();
            }}
            disabled={disableSaveFilter}
          >
            Save Filter
          </Button>
        )}
      </Box>
    </Box>
  );
};

const ServerAppGridPro: React.FunctionComponent<ServerAppGridProps> = (
  props,
) => {
  const { userId } = useSelector((state: AppState) => state.authReducer);
  const { setting: gridSettings } = useSelector(
    (state: AppState) => state.AppGridReducer,
  );
  const dispatch = useDispatch();
  const [saveSettingMutation] = useAddAppGridSettingMutation();
  const [isSaveFilterDialogOpen, setIsSaveFilterDialogOpen] =
    React.useState(false);
  const [shortcutResetSignal, setShortcutResetSignal] = React.useState(0);
  const skipNextShortcutResetRef = React.useRef(false);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string | React.ReactNode;
    severity: "success" | "error" | "warning" | "info";
  }>({ open: false, message: "", severity: "success" });

  const getGridSetting = () =>
    props.gridId === undefined
      ? undefined
      : gridSettings.find((s) => s.gridId === props.gridId);

  // Get column visibility model for this grid
  const getColumnVisibilityModel = (): GridColumnVisibilityModel => {
    const setting = getGridSetting();
    return setting?.columnVisibilityModel || {};
  };

  const handleSetVisibilityModel = (model: GridColumnVisibilityModel) => {
    if (props.gridId !== undefined) {
      const currentSetting = getGridSetting();
      const existingFilters = currentSetting?.filterSettings;
      const normalizedModel =
        Object.keys(model).length > 0
          ? model
          : props.columns.reduce((acc, col) => {
              acc[col.field] = true;
              return acc;
            }, {} as GridColumnVisibilityModel);

      dispatch(
        addGridSetting({
          setting: {
            gridId: props.gridId,
            columnVisibilityModel: normalizedModel,
            filterSettings: existingFilters,
          },
        }),
      );
      if (GridIdsToSave.includes(props.gridId)) {
        const settingPayload = {
          columnVisibilityModel: normalizedModel,
          filterSettings: Array.isArray(existingFilters)
            ? existingFilters
            : existingFilters
              ? [existingFilters]
              : [],
        };

        saveSettingMutation({
          data: {
            userId: userId ?? "",
            gridId: props.gridId,
            setting: JSON.stringify(settingPayload),
          },
        });
      }
    }
  };
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: props.paging.items || [],
    quickFilterValues: props.paging.quickFilterValues || [],
    linkOperator:
      props.paging.logicalOperator === "or"
        ? GridLinkOperator.Or
        : GridLinkOperator.And,
  });

  const hasActiveFilters = React.useMemo(() => {
    const hasFilterItems = (filterModel.items || []).some((item) =>
      isValidFilterItem(item),
    );

    return hasFilterItems;
  }, [filterModel]);

  const {
    getExistingFilterNames,
    getSavedFilters,
    persistFilterSettings,
    handleApplySavedFilter,
    handleDeleteSavedFilter,
    handleRenameSavedFilter,
    handleSaveFilter,
  } = useSavedFilterHandlers({
    gridId: props.gridId,
    gridSettings,
    dispatch,
    saveSettingMutation,
    userId,
    paging: props.paging,
    setPaging: props.setPaging,
    filterModel,
    setFilterModel,
    setSnackbar,
    onNewShortcutCreated: (filterName) => setShortcutResetSignal(filterName as any),
  });

  const { handleRemoveShortcut } = useSavedFilterShortcutHandlers({
    getSavedFilters,
    persistFilterSettings,
    setSnackbar,
  });

  const handleApplySavedFilterWithShortcutSync = (
    filter: AppFilterSettings,
  ) => {
    skipNextShortcutResetRef.current = true;
    handleApplySavedFilter(filter);
    setTimeout(() => (skipNextShortcutResetRef.current = false), 0);
    setShortcutResetSignal(
      (filter.isShortcut ? filter.filterShortcutName || "" : Date.now()) as any,
    );
  };

  const handleOpenSaveFilterDialog = () => {
    setIsSaveFilterDialogOpen(true);
  };

  const handleCloseSaveFilterDialog = () => {
    setIsSaveFilterDialogOpen(false);
  };

  const handleResetFilters = () => {
    const resetModel: GridFilterModel = {
      items: [],
      quickFilterValues: filterModel.quickFilterValues,
      linkOperator: GridLinkOperator.And,
    };

    setFilterModel(resetModel);

    props.setPaging({
      ...props.paging,
      pageIndex: 0,
      items: [],
      quickFilterValues: props.paging.quickFilterValues,
      logicalOperator: "and",
    });

    setShortcutResetSignal((prev) => prev + 1);
  };

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <>
        <label></label>
        <Pagination
          color="primary"
          count={pageCount}
          page={page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </>
    );
  }
  function CustomFooter() {
    return (
      <GridFooterContainer sx={{ width: props.hidePaginator ? "auto" : "80%" }}>
        {props.hidePaginator ? <></> : <CustomPagination />}
        <GridPagination {...({} as any)} />
      </GridFooterContainer>
    );
  }
  const handleGetColumns = (): GridColDef[] => {
    return props.columns.map((col) => ({
      ...col,
      sortable: col.sortable !== false, // Enable sorting by default unless explicitly disabled
    }));
  };

  return (
    <>
      <Card>
        <Box sx={{ height: props.height ?? 620, width: "100%" }}>
          <DataGridPro
            className={`${
              props.hidePaginationText ? "hide-pagination-text" : " "
            }`}
            getRowId={props.getRowId}
            rows={props.data}
            columns={handleGetColumns()}
            rowsPerPageOptions={
              props.hidePageSize
                ? [10, 20, 50, 100, 500, 1000]
                : [10, 20, 50, 100]
            }
            pagination
            paginationMode="server"
            onPageChange={(newPage) => {
              props.setPaging({
                ...props.paging,
                pageIndex: newPage,
              });
            }}
            onPageSizeChange={(newPageSize) => {
              props.setPaging({
                ...props.paging,
                pageSize: newPageSize,
                pageIndex: 0,
              });
            }}
            sortingMode="server"
            disableMultipleColumnsSorting
            sortModel={props.paging.sorting ? [props.paging.sorting] : []}
            filterMode="server"
            page={props.paging.pageIndex}
            selectionModel={props.selectedRows}
            pageSize={props.hidePageSize ? undefined : props.paging.pageSize}
            hideFooter={props.hideFooter ? true : false}
            filterModel={filterModel}
            columnVisibilityModel={getColumnVisibilityModel()}
            disableMultipleColumnsFiltering={false}
            onFilterModelChange={(model) => {
              setFilterModel(model);
              if (!skipNextShortcutResetRef.current)
                setShortcutResetSignal((prev) => prev + 1);
              else skipNextShortcutResetRef.current = false;
              const validItems = model.items.filter((item) =>
                isValidFilterItem(item),
              );

              const hasQuickFilterValues =
                model.quickFilterValues &&
                model.quickFilterValues.length > 0 &&
                model.quickFilterValues.some((val) => val.trim() !== "");

              const previousQuickFilterValues =
                props.paging.quickFilterValues &&
                props.paging.quickFilterValues.length > 0 &&
                props.paging.quickFilterValues.some(
                  (val: string) => val.trim() !== "",
                );

              const newPaging = {
                ...props.paging,
                pageIndex: 0,
                items: validItems.map((n, index) => ({
                  id: n.id ?? index,
                  columnField: n.columnField,
                  operatorValue: n.operatorValue ?? "",
                  value: n.value,
                })),
                quickFilterValues: model.quickFilterValues ?? [],
                logicalOperator: model.linkOperator || "and",
              };
              const hasActualFilters =
                validItems.length > 0 || hasQuickFilterValues;
              const hadPreviousFilters =
                (props.paging.items && props.paging.items.length > 0) ||
                previousQuickFilterValues;

              if (
                hasActualFilters ||
                (hadPreviousFilters && !hasActualFilters)
              ) {
                props.setPaging(newPaging);
                if (!hasActualFilters && hadPreviousFilters) {
                  setShortcutResetSignal((prev) => prev + 1);
                }
              }
            }}
            onSortModelChange={(model) => {
              props.setPaging({
                ...props.paging,
                pageIndex: 0,
                sorting: model.length > 0 ? model[0] : null,
              });
            }}
            rowCount={props.totalRecords}
            checkboxSelection={props.hideCheckBoxes ? false : true}
            disableSelectionOnClick
            loading={props.isLoading}
            onSelectionModelChange={(newSelection: any, detail: any) => {
              props.onSelect && props.onSelect(newSelection);
            }}
            localeText={{
              footerRowSelected: (count) =>
                count !== 1
                  ? `${count.toLocaleString()} rows selected`
                  : `${count.toLocaleString()} row selected`,
            }}
            components={{
              Toolbar: DefaultToolbar,
              Pagination: CustomFooter,
              FilterPanel: CustomFilterPanel,
            }}
            componentsProps={{
              toolbar: {
                customToolbar: props.customToolbar,
                switchLabel: props.switchLabel,
                switchChecked: props.switchChecked,
                onSwitchChange: props.onSwitchChange,
                manufacturerOptions: props.manufacturerOptions,
                selectedManufacturers: props.selectedManufacturers,
                onManufacturerChange: props.onManufacturerChange,
                savedFilters: getSavedFilters(),
                onApplySavedFilter: handleApplySavedFilterWithShortcutSync,
                onDeleteSavedFilter: handleDeleteSavedFilter,
                onRenameSavedFilter: handleRenameSavedFilter,
                onRemoveShortcut: handleRemoveShortcut,
                onResetFilters: handleResetFilters,
                isResetDisabled: !hasActiveFilters,
                resetSignal: shortcutResetSignal,
              },
              filterPanel: {
                onSaveFilter:
                  props.gridId !== undefined
                    ? handleOpenSaveFilterDialog
                    : undefined,
                disableSaveFilter: !hasActiveFilters,
              },
            }}
            isRowSelectable={props.isRowSelectable}
            keepNonExistentRowsSelected={
              props.keepNonExistentRowsSelected ?? true
            }
            disableColumnFilter={props.hideColumnFilter}
            onColumnVisibilityModelChange={handleSetVisibilityModel}
          />
        </Box>
      </Card>
      <SaveFilterDialog
        open={isSaveFilterDialogOpen}
        onClose={handleCloseSaveFilterDialog}
        onSave={handleSaveFilter}
        existingFilterNames={getExistingFilterNames()}
      />
      <SnackBarPopup
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={3000}
      />
    </>
  );
};

export default ServerAppGridPro;
